// Gallery Service for managing gallery data and Cloudinary operations

// Cloudinary configuration
const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'dwoubdppz';
const CLOUDINARY_UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'blog_uploads';

// Gallery categories mapping
export const GALLERY_CATEGORIES = {
  'showcase': {
    title: 'Showcase',
    slug: 'showcase',
    folder: 'show'
  },
  'kitchen-remodels': {
    title: 'Kitchens',
    slug: 'kitchen-remodels',
    folder: 'kitchen'
  },
  'bathroom-remodels': {
    title: 'Bathrooms',
    slug: 'bathroom-remodels',
    folder: 'bath'
  },
  'custom-woodwork': {
    title: 'Custom Woodwork',
    slug: 'custom-woodwork',
    folder: 'wood'
  },
  'other-projects': {
    title: 'Other Projects',
    slug: 'other-projects',
    folder: 'other'
  }
};

// Development helper for testing uploaded images locally
const getDevelopmentMockImages = (category) => {
  const mockImages = {
    'showcase': [
      { public_id: 'oak-and-stone-remodel/show/mock-showcase-1', context: { alt: 'Mock Showcase Image 1' } },
      { public_id: 'oak-and-stone-remodel/show/mock-showcase-2', context: { alt: 'Mock Showcase Image 2' } }
    ],
    'kitchen-remodels': [
      { public_id: 'oak-and-stone-remodel/kitchen/mock-kitchen-1', context: { alt: 'Mock Kitchen Image 1' } },
      { public_id: 'oak-and-stone-remodel/kitchen/mock-kitchen-2', context: { alt: 'Mock Kitchen Image 2' } }
    ],
    'bathroom-remodels': [
      { public_id: 'oak-and-stone-remodel/bath/mock-bathroom-1', context: { alt: 'Mock Bathroom Image 1' } },
      { public_id: 'oak-and-stone-remodel/bath/mock-bathroom-2', context: { alt: 'Mock Bathroom Image 2' } }
    ],
    'custom-woodwork': [
      { public_id: 'oak-and-stone-remodel/wood/mock-woodwork-1', context: { alt: 'Mock Woodwork Image 1' } },
      { public_id: 'oak-and-stone-remodel/wood/mock-woodwork-2', context: { alt: 'Mock Woodwork Image 2' } }
    ],
    'other-projects': [
      { public_id: 'oak-and-stone-remodel/other/mock-other-1', context: { alt: 'Mock Other Project Image 1' } },
      { public_id: 'oak-and-stone-remodel/other/mock-other-2', context: { alt: 'Mock Other Project Image 2' } }
    ]
  };
  
  return mockImages[category] || [];
};

// Fetch uploaded images from Cloudinary
export const fetchUploadedImages = async (category) => {
  try {
    const folderPath = `oak-and-stone-remodel/${GALLERY_CATEGORIES[category].folder}`;
    
    console.log('🔍 fetchUploadedImages called for category:', category);
    console.log('🔍 Folder path:', folderPath);
    console.log('🔍 Current hostname:', window.location.hostname);
    console.log('🔍 Current port:', window.location.port);
    console.log('🔍 NODE_ENV:', process.env.NODE_ENV);
    
    // Check if we're in development and Netlify functions aren't available
    const isDevelopment = process.env.NODE_ENV === 'development';
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const isNetlifyDev = window.location.port === '8888'; // Netlify dev runs on port 8888
    
    console.log('🔍 isDevelopment:', isDevelopment);
    console.log('🔍 isLocalhost:', isLocalhost);
    console.log('🔍 isNetlifyDev:', isNetlifyDev);
    
    if (isDevelopment && isLocalhost && !isNetlifyDev) {
      console.log('🔧 Development Mode: Netlify functions not available locally');
      console.log('💡 To test with uploaded images, run: npm run dev');
      console.log('💡 Or deploy to Netlify to see uploaded images in production');
      return getDevelopmentMockImages(category);
    }
    
    // Use Cloudinary Admin API to list resources
    console.log('🔍 Making API call to /api/list-images...');
    const response = await fetch(`/api/list-images`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        folder: folderPath,
        category: category
      }),
    });

    console.log('🔍 API Response status:', response.status);
    console.log('🔍 API Response ok:', response.ok);

    if (!response.ok) {
      console.error('Failed to fetch uploaded images:', response.status, response.statusText);
      
      if (isDevelopment) {
        console.log('💡 Development Note: Make sure you\'re running with "npm run dev" to test Netlify functions');
      }
      
      return [];
    }

    const data = await response.json();
    console.log('🔍 API Response data:', data);
    console.log(`✅ Successfully fetched ${data.resources?.length || 0} uploaded images for ${category}`);
    return data.resources || [];

  } catch (error) {
    console.error('Error fetching uploaded images:', error);
    
    if (process.env.NODE_ENV === 'development') {
      console.log('💡 Development Note: Make sure you\'re running with "npm run dev" to test Netlify functions');
    }
    
    return [];
  }
};

// Get optimized Cloudinary URL for uploaded images
export const getUploadedImageUrl = (publicId, size = 'medium') => {
  console.log('🔍 getUploadedImageUrl called with:', { publicId, size });
  
  const sizes = {
    thumbnail: { width: 200, height: 150 },
    small: { width: 400, height: 300 },
    medium: { width: 800, height: 600 },
    large: { width: 1200, height: 800 },
    xlarge: { width: 1600, height: 1200 }
  };

  const { width, height } = sizes[size] || sizes.medium;
  
  const url = `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/upload/w_${width},h_${height},c_fill,q_auto,f_auto/${publicId}`;
  console.log('🔍 Generated URL:', url);
  
  return url;
};

// Merge static and uploaded images
export const getMergedGalleryData = async (staticData, category) => {
  try {
    console.log('🔍 getMergedGalleryData called with:', { category, staticDataLength: staticData?.imageCollection?.length });
    
    const uploadedImages = await fetchUploadedImages(category);
    console.log('🔍 Uploaded images received:', uploadedImages);
    
    // Convert uploaded images to the same format as static data
    const uploadedImageUrls = uploadedImages.map(image => 
      getUploadedImageUrl(image.public_id, 'medium')
    );
    console.log('🔍 Uploaded image URLs:', uploadedImageUrls);

    // Merge static and uploaded images
    const mergedImages = [
      ...staticData.imageCollection,
      ...uploadedImageUrls
    ];
    console.log('🔍 Final merged images count:', mergedImages.length);

    return {
      ...staticData,
      imageCollection: mergedImages
    };

  } catch (error) {
    console.error('Error merging gallery data:', error);
    return staticData; // Return static data if merge fails
  }
};

// Upload image to Cloudinary
export const uploadImageToCloudinary = async (file, category) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
    formData.append('folder', `oak-and-stone-remodel/${GALLERY_CATEGORIES[category].folder}`);

    console.log('Uploading to Cloudinary:', {
      cloudName: CLOUDINARY_CLOUD_NAME,
      uploadPreset: CLOUDINARY_UPLOAD_PRESET,
      folder: `oak-and-stone-remodel/${GALLERY_CATEGORIES[category].folder}`,
      fileName: file.name,
      fileSize: file.size
    });

    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
      {
        method: 'POST',
        body: formData,
      }
    );

    if (!response.ok) {
      const errorData = await response.text();
      console.error('Cloudinary upload failed:', {
        status: response.status,
        statusText: response.statusText,
        errorData
      });
      throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log('Upload successful:', data);
    return {
      success: true,
      url: data.secure_url,
      public_id: data.public_id,
      width: data.width,
      height: data.height
    };

  } catch (error) {
    console.error('Upload error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Delete image from Cloudinary
export const deleteImageFromCloudinary = async (publicId) => {
  try {
    const response = await fetch('/api/delete-image', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ public_id: publicId }),
    });

    if (!response.ok) {
      throw new Error('Delete failed');
    }

    const data = await response.json();
    return {
      success: true,
      message: data.message
    };

  } catch (error) {
    console.error('Delete error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get optimized Cloudinary URL
export const getOptimizedImageUrl = (url, size = 'medium') => {
  if (!url || !url.includes('cloudinary.com')) {
    return url;
  }

  const sizes = {
    thumbnail: { width: 200, height: 150 },
    small: { width: 400, height: 300 },
    medium: { width: 800, height: 600 },
    large: { width: 1200, height: 800 },
    xlarge: { width: 1600, height: 1200 }
  };

  const { width, height } = sizes[size] || sizes.medium;

  // Insert transformation parameters into Cloudinary URL
  const urlParts = url.split('/upload/');
  if (urlParts.length === 2) {
    return `${urlParts[0]}/upload/w_${width},h_${height},c_fill,q_auto,f_auto/${urlParts[1]}`;
  }

  return url;
};

// Validate image file
export const validateImageFile = (file) => {
  const errors = [];

  // Check file type
  if (!file.type.startsWith('image/')) {
    errors.push('Please select an image file');
  }

  // Check file size (max 10MB)
  if (file.size > 10 * 1024 * 1024) {
    errors.push('File size must be less than 10MB');
  }

  // Check file dimensions (optional - can be done on client side)
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Get category options for dropdown
export const getCategoryOptions = () => {
  return Object.entries(GALLERY_CATEGORIES).map(([key, value]) => ({
    value: key,
    label: value.title
  }));
};

// Get category by slug
export const getCategoryBySlug = (slug) => {
  return Object.values(GALLERY_CATEGORIES).find(category => category.slug === slug);
};

// Get category by folder
export const getCategoryByFolder = (folder) => {
  return Object.values(GALLERY_CATEGORIES).find(category => category.folder === folder);
};

// Test upload preset configuration
export const testUploadPreset = async () => {
  try {
    // Create a proper test image blob
    const canvas = document.createElement('canvas');
    canvas.width = 100;
    canvas.height = 100;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ff0000';
    ctx.fillRect(0, 0, 100, 100);
    
    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.8));
    
    const formData = new FormData();
    formData.append('file', blob, 'test.jpg');
    formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);

    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
      {
        method: 'POST',
        body: formData,
      }
    );

    if (!response.ok) {
      const errorData = await response.text();
      console.error('Upload preset test failed:', {
        status: response.status,
        statusText: response.statusText,
        errorData
      });
      return {
        success: false,
        error: `Upload preset test failed: ${response.status} ${response.statusText}`
      };
    }

    const data = await response.json();
    console.log('Upload preset test successful:', data);
    return {
      success: true,
      message: 'Upload preset is working correctly'
    };

  } catch (error) {
    console.error('Upload preset test error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};
